import type { SettingsFlow, UpdateSettingsFlowBody } from "@ory/kratos-client";
import type { AxiosError } from "axios";
import type React from "react";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { Flow, handleFlowError } from "~/components/KratosFlow";
import { DefaultLayout } from "~/components/Layout";
import { Loading } from "~/components/Loading";
import { useModal } from "~/components/Modal";
import { useSnackBar } from "~/components/SnackBar";
import { kratos, SessionContext } from "~/ory";
import { StepBar } from "~/pages/Activate/StepBar";
import { createFlowErrorHandler } from "~/utils/errorHandler";

export const ActivateSetPassword: React.FC = () => {
	const session = useContext(SessionContext);
	const [openSnackBar] = useSnackBar();
	const [openModal, closeModal] = useModal();
	const [flow, setFlow] = useState<SettingsFlow | undefined>();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	// Get ?flow=... from the URL
	const returnTo = searchParams.get("return_to");
	const flowId = searchParams.get("flow");

	// In this effect we either initiate a new settings flow, or we fetch an existing settings flow.
	// biome-ignore lint/correctness/useExhaustiveDependencies: openSnackBar/openModal/closeModal are intentionally excluded. They are used only for fire-and-forget error display, and this effect should re-fetch on session/flow/URL changes only. They are stable in production (SnackBar/Modal providers memoize open/close via useCallback), so excluding them changes nothing at runtime; biome just cannot verify that stability across the context boundary.
	useEffect(() => {
		if (!session) return;
		// If the router is not ready yet, or we already have a flow, do nothing.
		if (flow) {
			return;
		}

		// Fetching/initializing a flow can fail at the network level (request aborted,
		// offline) with no HTTP response. handleFlowError can't classify those and re-rejects,
		// so we add a terminal handler to avoid an unhandled promise rejection (mirrors submit()).
		const onFlowError = createFlowErrorHandler(
			openSnackBar,
			openModal,
			closeModal,
		);

		// If ?flow=.. was in the URL, we fetch it
		if (flowId) {
			kratos
				.getSettingsFlow({ id: String(flowId) })
				.then(({ data }) => {
					// We received the flow - let's use its data and render the form!
					setFlow(data);
				})
				.catch(handleFlowError(navigate, "settings", setFlow))
				.catch(onFlowError);
			return;
		}

		// Otherwise we initialize it
		kratos
			.createBrowserSettingsFlow({
				returnTo: returnTo ? String(returnTo) : undefined,
			})
			.then(({ data }) => {
				setFlow(data);
			})
			.catch(handleFlowError(navigate, "settings", setFlow))
			.catch(onFlowError);
	}, [session, flowId, returnTo, flow, navigate]);

	/**
	 * biome-ignore lint: react-hooks/exhaustive-deps: if add openSnackBar to dependencies, will occur the following error
	 * Maximum update depth exceeded. This can happen when a component calls setState inside useEffect, but useEffect either doesn't have a dependency array, or one of the dependencies changes on every render.
	 * FIXME: fix design and correct dependencies
	 */
	useEffect(() => {
		const success = flow?.ui.messages?.find((m) => m.type === "success");
		if (success) {
			openSnackBar({ type: "success", text: success.text });
		}
	}, [flow]);

	if (!session) return <p>Unauthenticated</p>;
	if (!flow) return <Loading />;

	const submit = (values: UpdateSettingsFlowBody) => {
		setSearchParams({ flow: String(flow?.id) });
		return kratos
			.updateSettingsFlow({
				flow: String(flow?.id),
				updateSettingsFlowBody: values,
			})
			.then(({ data }) => {
				if (flow?.return_to) {
					window.location.href = flow.return_to;
					return;
				}
				if (data.state === "success") {
					return navigate("/settings");
				}
			})
			.catch(handleFlowError(navigate, "settings", setFlow))
			.catch((err: AxiosError<SettingsFlow>) => {
				// If the previous handler did not catch the error it's most likely a form validation error
				if (err.response?.status === 400) {
					// Yup, it is!
					setFlow(err.response?.data);
					return;
				}

				createFlowErrorHandler(openSnackBar, openModal, closeModal)(err);
			});
	};

	const onSubmit = (values: UpdateSettingsFlowBody) => submit(values);

	return (
		<>
			<title>Set Password | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer>
						<h1 className="mt-10 text-center font-normal">SET PASSWORD</h1>
						<div className="text-center mt-6">
							<StepBar step={3} />
						</div>
						<Flow
							onSubmit={onSubmit}
							flow={flow}
							only="password"
							context={{
								withIcon: true,
							}}
							setLabelTexts={[
								{
									query: { name: "method", type: "submit", value: "password" },
									value: "Update Password",
								},
							]}
						/>
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};
